import { useEffect, useMemo, useRef, useState } from 'react';
import { MenuProps as AntMenuProps } from 'antd';
import { useLocation } from 'react-router-dom';
import { useGlobal } from 'src/contexts/GlobalContext';
import { useTheme } from 'src/contexts/ThemeContext';
import { useAppNavigation } from 'src/routes';

import LogoCollapsed from '@/assets/logo-collapsed.svg?url';
import Logo from '@/assets/logo.svg?url';
import { DoubleRightOutlined } from '@ant-design/icons';

import { useSidebarItems } from '../const';
import { CollapseButton, LogoWrapper, MenuStyled, SiderStyled, Wrapper } from './Menu.styled';
import { transformDashToCamel } from 'src/utils/formatters';

export interface MenuProps {
  collapsed?: boolean;
  onClick?: AntMenuProps['onClick'];
  onClose?: () => void;
  onClickLogo?: () => void;
  toggleCollapsed?: () => void;
}

export const Menu = (props: MenuProps) => {
  const { onClick, collapsed, toggleCollapsed, onClose, onClickLogo } = props;
  const { isDarkMode } = useTheme();
  const { isDesktop } = useGlobal();
  const location = useLocation();
  const { navigateTo } = useAppNavigation();
  const [backgroundImage, setBackgroundImage] = useState('');

  const [selectedKeys, setSelectedKeys] = useState<string[]>();

  const isMenuNavigation = useRef(false);

  const handleOnClick: MenuProps['onClick'] = (e) => {
    if (onClick) {
      onClick(e);
    } else {
      const { key } = e;
      if (key in navigateTo) {
        isMenuNavigation.current = true;
        // @ts-expect-error - all keys are valid ? has something changed that we have error ?
        // eslint-disable-next-line
        navigateTo[key as keyof typeof navigateTo]();
        setSelectedKeys([key]);
      } else {
        console.error(`navigateTo[${key}] does not exist.`);
      }
    }
    onClose?.();
  };

  const handleClickLogo = () => {
    onClickLogo ? onClickLogo() : navigateTo.landing();
    onClose?.();
  };

  useEffect(() => {
    // Only load the image for the current theme
    if (isDarkMode) {
      import('@/assets/menu-dark.svg?url').then((module) => {
        setBackgroundImage(module.default);
      });
    } else {
      import('@/assets/menu-light.svg?url').then((module) => {
        setBackgroundImage(module.default);
      });
    }
  }, [isDarkMode]);

  const wicketLogo = useMemo(() => (collapsed ? LogoCollapsed : Logo), [collapsed]);

  const handleDevicesPath = () => {
    const secondPath = location.pathname.split('/')[2];
    if (secondPath) {
      return `${transformDashToCamel(secondPath)}`;
    }
    return '';
  };

  useEffect(() => {
    if (location.pathname.split('/')[1] === 'devices') {
      setSelectedKeys([handleDevicesPath()]);
    } else {
      setSelectedKeys([`${transformDashToCamel(location.pathname.split('/')[1])}`]);
    }
    isMenuNavigation.current = false;
  }, [location.pathname]);

  return (
    <Wrapper hasSider $isCollapsed={collapsed} $backgroundImage={backgroundImage}>
      <SiderStyled collapsed={collapsed}>
        <LogoWrapper $isCollapsed={collapsed} onClick={handleClickLogo}>
          <img src={wicketLogo} alt="logo" width={collapsed ? 'auto' : 171} height="23" />
        </LogoWrapper>
        <MenuStyled
          onClick={handleOnClick}
          defaultSelectedKeys={[`${location.pathname.split('/')[1]}`]}
          selectedKeys={selectedKeys}
          mode="inline"
          items={useSidebarItems()}
          $isCollapsed={collapsed}
          $isDarkMode={isDarkMode}
          $isDesktop={isDesktop}
        />
        {isDesktop && (
          <CollapseButton
            type="primary"
            onClick={toggleCollapsed}
            $isCollapsed={collapsed}
            icon={<DoubleRightOutlined />}
          />
        )}
      </SiderStyled>
    </Wrapper>
  );
};
